import React, { useReducer, useContext } from 'react';
import FeedbackContext from './feedbackContext';
import feedbackReducer from './feedbackReducer';
import AuthContext from '../auth/authContext';
import api from '../../utils/api';
import {
  GET_FEEDBACK,
  GET_USER_FEEDBACK,
  ADD_FEEDBACK,
  UPDATE_FEEDBACK,
  DELETE_FEEDBACK,
  ARCHIVE_FEEDBACK,
  FEEDBACK_ERROR,
  SET_LOADING,
  CLEAR_ERRORS,
  CLEAR_FEEDBACK
} from '../types';

const FeedbackState = props => {
  const initialState = {
    feedback: [],        // All feedback for admins
    userFeedback: [],    // Feedback for current user
    loading: false,
    error: null
  };

  const [state, dispatch] = useReducer(feedbackReducer, initialState);
  const { user } = useContext(AuthContext);

  // Get all feedback (admin only)
  const getAllFeedback = async () => {
    // Only allow authenticated admin users to fetch all feedback
    if (!user || !user.isAdmin || !localStorage.getItem('token')) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: 'Admin access required'
      });
      return;
    }

    try {
      setLoading();
      const res = await api.get('/api/feedback');
      
      dispatch({
        type: GET_FEEDBACK,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error fetching feedback'
      });
    }
  };

  // Get current user's feedback
  const getUserFeedback = async () => {
    // Only allow authenticated users to fetch their feedback
    if (!user || !localStorage.getItem('token')) {
      dispatch({
        type: GET_USER_FEEDBACK,
        payload: []
      });
      return;
    }

    try {
      setLoading();
      const res = await api.get('/api/feedback/user');
      
      dispatch({
        type: GET_USER_FEEDBACK,
        payload: res.data
      });
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error fetching your feedback'
      });
    }
  };

  // Add feedback
  const addFeedback = async (formData) => {
    // Only allow authenticated users to submit feedback
    if (!user || !localStorage.getItem('token')) {
      const error = 'You must be logged in to submit feedback';
      dispatch({
        type: FEEDBACK_ERROR,
        payload: error
      });
      throw new Error(error);
    }

    try {
      setLoading();
      const res = await api.post('/api/feedback', formData);
      
      dispatch({
        type: ADD_FEEDBACK,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error submitting feedback'
      });
      throw err;
    }
  };

  // Update feedback (admin response)
  const updateFeedback = async (id, formData) => {
    // Only allow authenticated admin users to update feedback
    if (!user || !user.isAdmin || !localStorage.getItem('token')) {
      const error = 'Admin access required';
      dispatch({
        type: FEEDBACK_ERROR,
        payload: error
      });
      throw new Error(error);
    }

    try {
      setLoading();
      const res = await api.put(`/api/feedback/${id}`, formData);
      
      dispatch({
        type: UPDATE_FEEDBACK,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error updating feedback'
      });
      throw err;
    }
  };

  // Delete feedback
  const deleteFeedback = async (id) => {
    // Only allow authenticated admin users to delete feedback
    if (!user || !user.isAdmin || !localStorage.getItem('token')) {
      const error = 'Admin access required';
      dispatch({
        type: FEEDBACK_ERROR,
        payload: error
      });
      throw new Error(error);
    }

    try {
      setLoading();
      await api.delete(`/api/feedback/${id}`);
      
      dispatch({
        type: DELETE_FEEDBACK,
        payload: id
      });
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error deleting feedback'
      });
      throw err;
    }
  };

  // Archive feedback
  const archiveFeedback = async (id) => {
    // Only allow authenticated admin users to archive feedback
    if (!user || !user.isAdmin || !localStorage.getItem('token')) {
      const error = 'Admin access required';
      dispatch({
        type: FEEDBACK_ERROR,
        payload: error
      });
      throw new Error(error);
    }

    try {
      setLoading();
      const res = await api.put(`/api/feedback/${id}/archive`);
      
      dispatch({
        type: ARCHIVE_FEEDBACK,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      dispatch({
        type: FEEDBACK_ERROR,
        payload: err.response?.data?.message || 'Error archiving feedback'
      });
      throw err;
    }
  };

  // Clear errors
  const clearErrors = () => dispatch({ type: CLEAR_ERRORS });

  // Clear feedback
  const clearFeedback = () => dispatch({ type: CLEAR_FEEDBACK });

  // Set loading
  const setLoading = () => dispatch({ type: SET_LOADING });

  return (
    <FeedbackContext.Provider
      value={{
        feedback: state.feedback,
        userFeedback: state.userFeedback,
        loading: state.loading,
        error: state.error,
        getAllFeedback,
        getUserFeedback,
        addFeedback,
        updateFeedback,
        deleteFeedback,
        archiveFeedback,
        clearErrors,
        clearFeedback
      }}
    >
      {props.children}
    </FeedbackContext.Provider>
  );
};

export default FeedbackState;